<template>
  <v-app>
    <v-container fluid>
      <v-row v-if="$insertAccess($route.path)">
        <v-col cols="12">
          <BaseCard
            :title="isEntryMode ? 'Add Employee Target' : 'Update Employee Target'"
            icon="mdi-target"
            vCardTextClass="pa-2"
          >
            <v-form @submit.prevent="submit" ref="addSalesEmployeeTarget" id="addSalesEmployeeTarget">
              <v-row>
                <v-col md="3" cols="12">
                  <v-card elevation="0" border rounded="lg" class="pa-2 h-100">
                    <div class="d-flex align-center mb-4">
                      <v-avatar color="primary" variant="tonal" size="36" class="me-2">
                        <v-icon size="20">mdi-information-outline</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-subtitle-1 font-weight-medium">Main details</div>
                        <div class="text-caption text-medium-emphasis">Provide main information</div>
                      </div>
                    </div>
                    <v-row dense>
                      <v-col cols="12">
                        <v-autocomplete
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          v-model="employee_id.value.value"
                          :items="getHrmEmployeePermissionWiseList"
                          :item-title="item => `${item.code}-${item.name}` ?? 'N/A'"
                          item-value="id"
                          :disabled="employeeDisable"
                          :error-messages="employee_id.errorMessage.value"
                          autocomplete="off"
                          :loading="employee_loading"
                          clearable
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
                        >
                          <template #label>
                            Employee
                            <span class="text-red-accent-4">*</span>
                          </template>
                        </v-autocomplete>

                        <v-date-input
                          input-format="dd/mm/yyyy"
                          density="compact"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          variant="outlined"
                          bg-color="grey-lighten-4"
                          v-model="start_date.value.value"
                          :error-messages="start_date.errorMessage.value"
                          autocomplete="off"
                          clearable
                        >
                          <template #label>
                            Start Date
                            <span class="text-red-accent-4">*</span>
                          </template>
                        </v-date-input>

                        <v-date-input
                          input-format="dd/mm/yyyy"
                          density="compact"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          variant="outlined"
                          bg-color="grey-lighten-4"
                          v-model="end_date.value.value"
                          :error-messages="end_date.errorMessage.value"
                          autocomplete="off"
                          clearable
                        >
                          <template #label>
                            End Date
                            <span class="text-red-accent-4">*</span>
                          </template>
                        </v-date-input>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>

                <v-col class="pl-0" md="9" cols="12">
                  <v-card elevation="0" border rounded="lg">
                    <div class="d-flex align-center justify-space-between pa-2">
                      <div class="d-flex align-center">
                        <v-avatar color="primary" variant="tonal" size="36" class="me-2">
                          <v-icon size="20">mdi-format-list-bulleted</v-icon>
                        </v-avatar>
                        <div>
                          <div class="text-subtitle-1 font-weight-medium">Employee Target details</div>
                          <div class="text-caption text-medium-emphasis">
                            Add target breakdown by customer or product subcategory
                          </div>
                        </div>
                      </div>
                      <div>
                        <v-number-input
                            v-model="getTotalTargetAmount"
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            class="ma-0"
                            control-variant="hidden"
                            style="width: 200px !important"
                            :error-messages="totalTargetError"
                            readonly
                        >
                          <template #label>
                            Total Target Amount
                            <span class="text-red-accent-4">*</span>
                          </template>
                        </v-number-input>
                      </div>
                    </div>

                    <div v-if="!contacts.length" class="text-center py-8">
                      <v-icon size="64" color="grey-darken-1" class="mb-2">
                        mdi-target
                      </v-icon>
                      <div class="text-body-2 text-medium-emphasis mb-4">
                        No target added yet
                      </div>
                      <v-btn @click="addContactRow" variant="tonal" color="primary">
                        <v-icon color="info" icon="mdi-plus" size="large" class="mr-2"></v-icon>
                        Add Details
                      </v-btn>
                    </div>

                    <template v-else>
                      <v-row
                        v-for="(contact, index) in contacts"
                        :key="index"
                        class="px-2 overflow-x-auto"
                        align="center"
                        dense
                      >
                        <v-col cols="12" md="3">
                          <v-autocomplete
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            v-model="contact.target_type"
                            hide-details="auto"
                            :items="target_type"
                            :item-title="(item) => `${item.text}`"
                            item-value="value"
                            autocomplete="off"
                            clearable
                            :menu-props="{ contentClass: 'my-custom-dropdown' }"
                          >
                            <template #label>
                              Target Type
                              <span class="text-red-accent-4">*</span>
                            </template>
                          </v-autocomplete>
                        </v-col>

                        <v-col
                          v-if="contact.target_type === 'Customer'"
                          cols="12" md="3"
                        >
                          <v-autocomplete
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            v-model="contact.customer_id"
                            :items="getCustomerList"
                            :item-title="(item) => `${item.company_name}`"
                            item-value="id"
                            hide-details="auto"
                            :loading="customer_loading"
                            autocomplete="off"
                            :menu-props="{ contentClass: 'my-custom-dropdown' }"
                          >
                            <template #label>
                              Customer Name
                              <span class="text-red-accent-4">*</span>
                            </template>
                          </v-autocomplete>
                        </v-col>

                        <v-col
                          v-if="contact.target_type === 'Category'"
                          cols="12" md="3"
                        >
                          <v-autocomplete
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            v-model="contact.subcategory_id"
                            :items="getSubCategoryList"
                            :item-title="item => `${item.code} - ${item.name}` ?? 'N/A'"
                            item-value="id"
                            hide-details="auto"
                            :loading="category_loading"
                            autocomplete="off"
                            :menu-props="{ contentClass: 'my-custom-dropdown' }"
                          >
                            <template #label>
                              Sub Category
                              <span class="text-red-accent-4">*</span>
                            </template>
                          </v-autocomplete>
                        </v-col>

                        <v-col cols="6" sm="4" md="3">
                          <v-number-input
                            v-model="contact.target_amount"
                            variant="outlined"
                            hide-details="auto"
                            density="compact"
                            bg-color="grey-lighten-4"
                            class="ma-0"
                            control-variant="hidden"
                            clearable
                          >
                            <template #label>
                              Target Amount
                              <span class="text-red-accent-4">*</span>
                            </template>
                          </v-number-input>
                        </v-col>

                        <v-col cols="6" sm="4" md="2">
                          <v-number-input
                            v-model="contact.target_quantity"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            bg-color="grey-lighten-4"
                            class="ma-0"
                            control-variant="hidden"
                          >
                            <template #label>Target Quantity</template>
                          </v-number-input>
                        </v-col>

                        <v-col sm="4" md="1" class="d-flex flex-column align-center justify-center">
                          <v-btn
                            icon="mdi-delete-outline"
                            size="small"
                            color="error"
                            variant="text"
                            @click="removeContactRow(index)"
                          ></v-btn>

                          <v-btn
                            v-if="index == contacts.length - 1"
                            icon="mdi-plus"
                            size="small"
                            color="info"
                            variant="text"
                            @click="addContactRow"
                          ></v-btn>
                        </v-col>

                        <v-divider
                          v-if="contacts.length > 1 && index !== contacts.length - 1"
                        ></v-divider>
                      </v-row>
                    </template>
                  </v-card>
                </v-col>
              </v-row>
            </v-form>

            <v-divider class="mt-2"></v-divider>
            <v-card-actions>
              <v-row align="start">
                <v-col cols="12" md="8">
                  <v-textarea
                    label="Remarks"
                    density="compact"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="remarks.value.value"
                    :error-messages="remarks.errorMessage.value"
                    autocomplete="off"
                    rows="3"
                    clearable
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="d-flex justify-end ga-2">
                    <v-btn
                      color="grey-darken-1"
                      variant="flat"
                      prepend-icon="mdi-content-save-outline"
                      @click="submitForm(0)"
                    >
                      draft
                    </v-btn>
                    <v-btn
                      color="primary"
                      variant="flat"
                      prepend-icon="mdi-send-outline"
                      @click="submitForm(1)"
                    >
                      {{ isEntryMode ? "Post" : "Update" }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </BaseCard>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref, reactive, getCurrentInstance, watch } from "vue";
import { toast } from "vue3-toastify";
import { useRouter } from "vue-router";
import formattedDate from "@/plugins/dateFormatter";
import salesDepartmentTarget from "@/store/module/sales/targetManagement/salesDepartmentTarget";
import BaseCard from "@/components/utils/baseCard.vue";

export default {
    components: { BaseCard },
    data: () => ({
        isEntryMode: true,
        itemsPerPage: 10,
        manualTargetAmount:0,

        loading: false,
        deparment_loading: false,
        customer_loading: false,
        category_loading: false,
        team_loading: false,
        sale_team_target_loading: false,
        employee_loading: false,
        
        search: "",
        data: {},

      employeeDisable: false,

        departmentTargetId: null,
        salesTeamId: null,

        target_type: [
            { text: "Customer", value: "Customer" },
            { text: "Product Sub Category", value: "Category" },
        ],
    }),
    watch: {

    },
    created() {
        // if(this.$store.getters.getHrmEmployeePermissionWiseList.length == 0) {
            this.employee_loading = true;
            this.$store.dispatch("getHrmEmployeePermissionWiseList", {params:{is_all_branch: true }}).then((response) => {
                this.employee_loading = false;
            });
        // }
        // if (this.$store.getters.getCustomerList.length == 0) {
            this.customer_loading = true;
            this.$store.dispatch("getCustomerList").then((response) => {
                this.customer_loading = false;
            });
        // }
        // if (this.$store.getters.getSubCategoryList.length == 0) {
            this.category_loading = true;
            this.$store.dispatch("getSubCategoryList").then((response) => {
                this.category_loading = false;
            });
        // }
    },

    computed: {
        getTotalTargetAmount: {
            get() {
                return this.contacts.length ? this.contacts.reduce((acc, item) => acc + (Number(item.target_amount) || 0), 0) : Number(this.manualTargetAmount);
            },
            set(newValue) {
                // if (!this.contacts.length) {
                //     this.contacts.push({
                //         target_type: "Product",
                //         customer_id: null,
                //         subcategory_id: null,
                //         target_amount: newValue,
                //         target_quantity: null,
                //     });
                //     return;
                // }
                // this.contacts[0].target_amount = Number(newValue);
                this.manualTargetAmount = Number(newValue);
            }
        },
        getHrmDepartmentList() {
            return this.$store.getters.getHrmDepartmentList;
        },
        getCustomerList() {
            return this.$store.getters.getCustomerList;
        },
        getSubCategoryList() {
            return this.$store.getters.getSubCategoryList;
        },
        getSalesTeams() {
            return this.$store.getters.getSalesTeams;
        },
        getSalesTeamTargetList() {
            return this.$store.getters.getSalesTeamTargetList;
        },
        getHrmEmployeePermissionWiseList() {
            return this.$store.getters.getHrmEmployeePermissionWiseList;
        },
    },

    methods: {
        resetForm() {
            this.data = {};
        },
    },

    setup() {
        const { proxy } = getCurrentInstance();

        const { handleSubmit, resetForm } = useForm({
            validationSchema: yup.object({
                employee_id: yup.string().required().label("Employee"),
                start_date: yup.date().required().label("Start Date"),
                end_date: yup.date().required().label("End Date"),
            }),
        });

        const sales_target_master_id = useField("sales_target_master_id");
        const employee_id = useField("employee_id");
        const start_date = useField("start_date");
        const end_date = useField("end_date");
        const remarks = useField("remarks");
        const status = useField("status");
        const target_details = useField("target_details");

        const contacts = ref([
            // {
            //     target_type: "Product",
            //     customer_id: null,
            //     subcategory_id: null,
            //     target_amount: null,
            //     target_quantity: null,
            // },
        ]);

        const currentStatus = ref(0);

        const contactErrors = reactive({});
        const errorMessages = ref("");
        const loading = ref("");
        const isEntryMode = ref(true);
        const totalTargetError = ref("");

        const store = useStore();
        const router = useRouter();

        const addContactRow = () => {
            contacts.value.push({
                target_type: "Category",
                customer_id: null,
                subcategory_id: null,
                target_amount: null,
                target_quantity: null,
            });
        };
        const removeContactRow = (index) => {
            if (contacts.value.length >= 1) {
                contacts.value.splice(index, 1);
            }
        };

        const submitForm = (status) => {
            currentStatus.value = status;
            const totalAmount = proxy.getTotalTargetAmount;
            if (!totalAmount || Number(totalAmount) <= 0) {
                totalTargetError.value = "Total Target Amount must be greater than 0";
            } else {
                totalTargetError.value = "";
            }
            submit();
        };

        const submit = handleSubmit((values) => {
            const totalAmount = proxy.getTotalTargetAmount;
            const invalidTargetAmount = contacts.value.find(
                item => !item.target_amount 
            );

            const invalidRow = contacts.value.find(
                (item) => (item.target_type === "Customer" && !item.customer_id) || (item.target_type === "Category" && !item.subcategory_id)
            );

            if (invalidTargetAmount && contacts.value.length === 1 ) {
                toast.error("Every Detail must have Target Amount");
                return;
            } else if (invalidRow || invalidTargetAmount) {
                if(contacts.value.length >= 1 && invalidTargetAmount){
                    toast.error("Every Detail must have Target Amount");
                    return;
                }
                toast.error("Every Detail must have Customer or Product Sub Category Info");
                return;
            }
            
            values.status = currentStatus.value;
            values.target_details = contacts.value || [];
            values.start_date = formattedDate(start_date.value.value);
            values.end_date = formattedDate(end_date.value.value);
            values.target_amount = totalAmount;

            loading.value = true;
            store
                .dispatch("saveUpdateSalesEmployeeTarget", values)
                .then((response) => {
                    resetForm();
                    contacts.value = [
                        // {
                        //     target_type: "Product",
                        //     customer_id: null,
                        //     subcategory_id: null,
                        //     target_amount: null,
                        //     target_quantity: null,
                        // },
                    ];
                    loading.value = false;
                    toast.success(response.data.message, {
                        timeout: 3000,
                    });
                    setTimeout(() => {
                        router.push({ name: "employee-target-records" });
                    }, 2000);
                })
                .catch((error) => {
                    loading.value = false;
                    toast.error(error.response?.data?.message || "An error occurred");
                });
        });

        return {
            submit,
            submitForm,

            sales_target_master_id,
            employee_id,
            start_date,
            end_date,
            target_details,
            remarks,
            status,

            contacts,
            addContactRow,
            removeContactRow,
            errorMessages,
            isEntryMode,
            resetForm,
            loading,
            contactErrors,
            totalTargetError,
        };
    },

    mounted() {
        const employee_id = this.$store.getters.getAuthUser?.employee?.id;
        console.log(this.$store.getters.getAuthUser);
        if (this.$store.getters.getEditingSalesEmployeeTarget) {
            this.isEntryMode = false;
            const data = this.$store.getters.getEditingSalesEmployeeTarget;
          console.log(data)

            this.sales_target_master_id.value.value = data?.id;
            this.employee_id.value.value = data.employee_id;
            this.start_date.value.value = data?.start_date;
            this.end_date.value.value = data.end_date;
            this.manualTargetAmount = data.target_amount || 0;
            this.remarks.value.value = data.remarks;

            this.contacts = data.sales_target_details?.length ? data.sales_target_details.map((detail) => ({
                target_type: detail.target_type || "Category",
                customer_id: detail.customer_id || null,
                subcategory_id: detail.subcategory_id || null,
                target_amount: Number(detail.target_amount) || 0,
                target_quantity: Number(detail.target_quantity) || 0,
            }))
            : [];

        }else{
            this.isEntryMode = true;
            this.resetForm();
            this.contacts = [];
            if(employee_id) {
              this.employee_id.value.value = employee_id
              this.employeeDisable = true;
            } else {
              this.employeeDisable = false;
            }
        }
    },
    beforeUnmount() {
        this.$store.commit("seteditingSalesEmployeeTarget", null);
    },
    
};
</script>
